import type {
  OrgCompany,
  OrgStructureLevelRow,
  OrgValidationNode,
  OrgValidationTree,
} from "@/api/diagnostic-org-structure";

/**
 * A árvore da estrutura organizacional validada no Diagnóstico (#1342).
 *
 * Funções puras sobre o contrato do `GET /diagnostic/org-structure`. Não reusa
 * `org-structure-tree.ts`: aquele monta a árvore do Configuration Server a partir
 * de níveis aninhados e `id/parent_id`, e este contrato vem achatado, com a chave
 * natural (`item_key`/`parent_key`) e as company codes de cada unidade já
 * resolvidas pelo backend.
 *
 * O filtro é a lista de `id` de company code (o `BUKRS`) escolhidas; vazia, todas.
 */

/** Marca do backend na unidade sem empresa conhecida: o grupo de compradores e a órfã. */
const ALL_COMPANY_CODES = "*";
const COMPANY_CODE = "company_code";

/**
 * Os níveis que o SAP não liga a empresa nenhuma, e que vão para o grupo "Sem
 * vínculo com empresa no SAP". É pelo nível, e não pelo `*`: o backend também
 * manda `*` quando não conseguiu saber (extração sem a TKA02, unidade órfã), e
 * aí a unidade tem empresa, só que desconhecida.
 */
const UNLINKED_LEVELS: ReadonlySet<string> = new Set(["purchasing_group"]);

/**
 * Os níveis que ficam num bloco próprio no fim da árvore, e não debaixo do pai:
 * a organização de compras, como o CBC mostra em "Set Up Organizational
 * Structure" (resposta do especialista em 15/10/2026). Ela atende empresas pelos
 * centros, e debaixo de uma company code só ela pareceria ser de uma empresa só.
 * O filtro por empresa continua pelo `company_codes` do backend.
 */
const SEPARATE_LEVELS: readonly string[] = ["purchasing_organization"];
const SHARED_GROUP = "shared";

/**
 * Os 15 tipos do template da EF. Só eles ganham a linha "Nenhum(a) … no legado":
 * o catálogo tem mais dois (`sales_area` e `warehouse_number`), que aparecem se
 * tiverem objeto, mas cuja ausência o template não pede para declarar.
 */
const TEMPLATE_LEVELS: ReadonlySet<string> = new Set([
  "operating_concern",
  "controlling_area",
  "credit_control_area",
  "company",
  "company_code",
  "purchasing_organization",
  "purchasing_group",
  "plant",
  "storage_location",
  "shipping_point",
  "sales_organization",
  "distribution_channel",
  "division",
  "sales_office",
  "sales_group",
]);

/** Uma unidade na árvore. A unidade N:M aparece uma vez debaixo de cada pai visível. */
export interface OrgUnitRow {
  kind: "unit";
  /** Única na árvore: inclui o caminho, porque a unidade N:M se repete. */
  key: string;
  node: OrgValidationNode;
  level: OrgStructureLevelRow;
  transversal: boolean;
  /**
   * Casa com a busca (D43). Sem busca, toda unidade casa. Quem entra só como
   * ancestral de quem casa, para dar contexto, vem `false` e não é selecionável.
   */
  matched: boolean;
  /** Quantos pais N:M a unidade tem; 0 quando o pai é único. */
  parentCount: number;
  children: OrgTreeRow[];
}

/** "Nenhum(a) … encontrado(a) no legado": nível do template sem objeto ali. */
export interface OrgEmptyRow {
  kind: "empty";
  key: string;
  level: OrgStructureLevelRow;
}

/**
 * O que o SAP não liga a empresa nenhuma, junto numa linha só no fim da árvore,
 * recolhida: o grupo de compradores (a T024 não tem empresa). São centenas num
 * sistema com conteúdo SAP, e soltos eles empurravam as empresas para fora da tela.
 */
export interface OrgGroupRow {
  kind: "group";
  key: string;
  /** `shared` para o que não tem empresa no SAP, ou o nível do bloco próprio. */
  group: string;
  /**
   * O grupo `shared` com as empresas vindas do uso, e não do cadastro: o grupo de
   * compradores com documento de compra no período (EKKO, #1342). Sem a leitura
   * do uso, eles chegam com `*` e o grupo continua "sem vínculo".
   */
  byUsage: boolean;
  /** Quantos objetos o grupo guarda, contando os que estão abaixo das raízes. */
  count: number;
  children: OrgTreeRow[];
}

export type OrgTreeRow = OrgUnitRow | OrgEmptyRow | OrgGroupRow;

export interface OrgIndicators {
  companyCodes: { total: number; inScope: number; outOfScope: number; open: number };
  validation: {
    total: number;
    inScope: number;
    clientValidation: number;
    outOfScope: number;
    pending: number;
    /** (em escopo + fora do escopo) ÷ total, arredondado. "Validar com cliente" ainda não é decisão. */
    decidedPct: number;
  };
  /** Objetos marcados "Não" em Confere?. */
  mismatches: number;
  /** Só os tipos com objeto, na ordem do template. */
  byType: Array<{ levelCode: string; count: number }>;
}

/** Identidade da unidade, a mesma do backend: `(item_type, item_key)`. */
function unitId(itemType: string, itemKey: string): string {
  return `${itemType}:${itemKey}`;
}

/** A identidade de um nó, a mesma da seleção do lote e da prévia da propagação. */
export function orgUnitId(node: { item_type: string; item_key: string }): string {
  return unitId(node.item_type, node.item_key);
}

/** O caminho de volta: o tipo nunca tem `:`, a chave pode ter. */
export function orgUnitRef(id: string): { item_type: string; item_key: string } {
  const at = id.indexOf(":");
  return { item_type: id.slice(0, at), item_key: id.slice(at + 1) };
}

function byId(a: OrgCompany, b: OrgCompany): number {
  return a.id.localeCompare(b.id, undefined, { numeric: true });
}

/**
 * As empresas que o select oferece. Com a regra de empresa-modelo aplicada, só as do
 * cliente; sem ela (`is_client` nulo), todas, porque não há como separar.
 */
export function companyTabs(tree: OrgValidationTree): OrgCompany[] {
  return tree.companies.filter((company) => company.is_client !== false).sort(byId);
}

/**
 * As unidades que o filtro mostra. É a mesma lista para a árvore e para os
 * indicadores, para eles nunca contarem o que a tabela não desenha.
 *
 * A visibilidade base vem do api (`visible`, spec 2026-10-26): a unidade só de
 * empresas-modelo (inclusive a company code-modelo), a empresa (T880) só de
 * empresas-modelo e a unidade que nenhuma empresa usa (com a exceção do demo, D44 de
 * 23/10) moram lá, na mesma regra da sincronização com o Configuration Server.
 * O nível do nó é lido para saber se ele é transversal; um tipo que não esteja no
 * catálogo de níveis é descartado (não há onde desenhá-lo), guarda técnica que pelo
 * GET não dispara, porque o catálogo é o mesmo da extração. Aqui fica só o que
 * depende da escolha do usuário:
 *
 * - O `*` do backend quer dizer "não dá para saber de qual empresa": o grupo de
 *   compradores, que o SAP não liga a empresa, e a unidade órfã. O de nível
 *   transversal aparece em todos os filtros; a órfã, só sem filtro (todas as
 *   empresas).
 * - Com filtro, a unidade aparece se é de alguma das empresas escolhidas (a união).
 */
function visibleNodes(tree: OrgValidationTree, filter: readonly string[]): OrgValidationNode[] {
  const filtering = filter.length > 0;
  const chosen = new Set(filter);
  const levels = new Map(tree.levels.map((level) => [level.level_code, level]));

  return tree.nodes.filter((node) => {
    if (!node.visible) return false;
    const level = levels.get(node.item_type);
    if (!level) return false;
    const codes = node.company_codes;
    if (codes.includes(ALL_COMPANY_CODES)) return level.transversal || !filtering;
    if (filtering) return codes.some((code) => chosen.has(code));
    return true;
  });
}

/**
 * Monta a floresta, na ordem dos níveis do catálogo e, dentro do nível, da chave legado.
 *
 * Unidade cujo pai não veio (ou ficou fora do filtro) vira raiz: nada some da
 * árvore por um vínculo que a extração não resolveu. A unidade N:M (pai nulo e
 * `associations`) aparece debaixo de cada pai visível, e é a mesma decisão nas
 * duas cópias.
 *
 * Com filtro, a árvore é a mesma do template, só com o que é de alguma company
 * code escolhida: desde a #1399, o transversal chega com as company codes que estão
 * abaixo dele no SAP, e o filtro já o tira quando não é dela. O grupo de
 * compradores vai para o grupo do fim, com ou sem filtro.
 *
 * Com `search` (D43), depois do filtro de empresas, ficam as unidades cujo nome, id
 * ou chave legado contém o texto e os ancestrais delas, só como contexto
 * (`matched: false`). Sem nada que case, a árvore vem vazia, sem as linhas de
 * "nenhum encontrado": elas descrevem a árvore inteira, não o resultado da busca.
 */
export function buildOrgTree(
  tree: OrgValidationTree,
  filter: readonly string[],
  search = "",
): OrgTreeRow[] {
  const levels = new Map(tree.levels.map((level) => [level.level_code, level]));
  const needle = search.trim().toLowerCase();
  const searching = needle !== "";
  const visible = visibleNodes(tree, filter);
  const found = searching ? narrowToSearch(visible, levels, needle) : null;
  const nodes = found ? found.nodes : visible;
  const present = new Set(nodes.map((node) => unitId(node.item_type, node.item_key)));

  const childrenOf = new Map<string, OrgValidationNode[]>();
  const roots: OrgValidationNode[] = [];
  // `push`, e não cópia: um pai com milhares de filhos (depósitos de um centro)
  // copiaria o array a cada inserção.
  const attach = (parent: string, node: OrgValidationNode) => {
    const children = childrenOf.get(parent);
    if (children) children.push(node);
    else childrenOf.set(parent, [node]);
  };

  const separate = new Map<string, OrgValidationNode[]>();
  for (const node of nodes) {
    if (SEPARATE_LEVELS.includes(node.item_type)) {
      separate.set(node.item_type, [...(separate.get(node.item_type) ?? []), node]);
      continue;
    }
    const parentLevel = levels.get(node.item_type)?.parent_level_code;
    const parent =
      node.parent_key && parentLevel ? unitId(parentLevel, node.parent_key) : null;
    const associated = node.associations
      .map((ref) => unitId(ref.item_type, ref.item_key))
      .filter((ref) => present.has(ref));

    if (parent && present.has(parent)) attach(parent, node);
    else if (associated.length > 0) associated.forEach((ref) => attach(ref, node));
    else roots.push(node);
  }

  const sequence = (code: string) => levels.get(code)?.sequence ?? Number.MAX_SAFE_INTEGER;
  const ordered = (list: OrgValidationNode[]) =>
    [...list].sort(
      (a, b) =>
        sequence(a.item_type) - sequence(b.item_type) ||
        a.id.localeCompare(b.id, undefined, { numeric: true }),
    );

  // Com a linha de vazio no lugar do nível, na ordem do template. O grupo dos
  // transversais nasce depois, então aqui só há unidade e vazio.
  type LevelRow = OrgUnitRow | OrgEmptyRow;
  const withEmpty = (
    rows: LevelRow[],
    expected: OrgStructureLevelRow[],
    path: string,
  ): LevelRow[] => {
    if (searching) return rows;
    const shown = new Set(rows.map((row) => row.level.level_code));
    const empty: OrgEmptyRow[] = expected
      .filter((level) => TEMPLATE_LEVELS.has(level.level_code) && !shown.has(level.level_code))
      .map((level) => ({ kind: "empty", key: `${path}/empty:${level.level_code}`, level }));
    return [...rows, ...empty].sort(
      (a, b) => a.level.sequence - b.level.sequence,
    );
  };

  const toRow = (node: OrgValidationNode, path: string, seen: Set<string>): OrgUnitRow => {
    const id = unitId(node.item_type, node.item_key);
    const key = `${path}/${id}`;
    const level = levels.get(node.item_type)!;
    const inner = new Set(seen).add(id);
    let children: LevelRow[] = ordered(childrenOf.get(id) ?? [])
      .filter((child) => !inner.has(unitId(child.item_type, child.item_key)))
      .map((child) => toRow(child, key, inner));
    // Só a company code declara os níveis vazios abaixo dela. Mais fundo, a
    // ausência fica implícita: centro sem depósito não precisa de linha própria.
    if (node.item_type === COMPANY_CODE) {
      const below = tree.levels.filter(
        (lv) => lv.parent_level_code === COMPANY_CODE && !SEPARATE_LEVELS.includes(lv.level_code),
      );
      children = withEmpty(children, below, key);
    }
    return {
      kind: "unit",
      key,
      node,
      level,
      transversal: level.transversal,
      matched: found ? found.matched.has(id) : true,
      parentCount: node.parent_key ? 0 : node.associations.length,
      children,
    };
  };

  const rootRows = ordered(roots).map((node) => toRow(node, "", new Set()));
  const transversal = tree.levels.filter((level) => level.transversal);
  // Nível transversal vazio só conta se não houver nenhum objeto dele na árvore,
  // nem como filho (a área de contabilidade de custos mora debaixo da de resultado).
  const anywhere = new Set(nodes.map((node) => node.item_type));
  const missing = transversal.filter((level) => !anywhere.has(level.level_code));
  const all = withEmpty(rootRows, missing, "");

  const unlinked = (row: OrgTreeRow) =>
    row.kind === "unit" && UNLINKED_LEVELS.has(row.node.item_type);
  const linked = all.filter((row) => !unlinked(row));
  const shared = all.filter(unlinked);

  // O bloco próprio: o grupo com os objetos, ou a linha de vazio no lugar dele.
  const blocks: OrgTreeRow[] = [];
  for (const levelCode of SEPARATE_LEVELS) {
    const level = levels.get(levelCode);
    if (!level) continue;
    const path = `/group:${levelCode}`;
    const rows = ordered(separate.get(levelCode) ?? []).map((node) =>
      toRow(node, path, new Set()),
    );
    if (rows.length > 0) {
      blocks.push({
        kind: "group",
        key: path,
        group: levelCode,
        byUsage: false,
        count: countUnits(rows),
        children: rows,
      });
    } else if (!searching && TEMPLATE_LEVELS.has(levelCode)) {
      blocks.push({ kind: "empty", key: `/empty:${levelCode}`, level });
    }
  }

  if (shared.length === 0) return [...linked, ...blocks];
  return [
    ...linked,
    ...blocks,
    {
      kind: "group",
      key: `/group:${SHARED_GROUP}`,
      group: SHARED_GROUP,
      byUsage: shared.some(
        (row) =>
          row.kind === "unit" &&
          row.node.company_codes.length > 0 &&
          !row.node.company_codes.includes(ALL_COMPANY_CODES),
      ),
      count: countUnits(shared),
      children: shared,
    },
  ];
}

/** O texto em que a busca procura: nome, id e chave legado do objeto. */
function searchText(node: OrgValidationNode): string {
  return `${node.name ?? ""} ${node.id} ${node.item_key}`.toLowerCase();
}

/**
 * Da busca (D43): as unidades visíveis que casam com o texto (`needle`, já em
 * minúsculas) e os ancestrais delas, que entram só como contexto. O pai é o mesmo
 * que a árvore usa (`parent_key` no nível pai; sem ele, os pais da N:M que estão
 * visíveis). O nível que a árvore guarda em bloco próprio não tem ancestral: ele
 * não é desenhado debaixo do pai.
 */
function narrowToSearch(
  visible: OrgValidationNode[],
  levels: Map<string, OrgStructureLevelRow>,
  needle: string,
): { nodes: OrgValidationNode[]; matched: Set<string> } {
  const byUnit = new Map(visible.map((node) => [unitId(node.item_type, node.item_key), node]));
  const matched = new Set<string>();
  for (const [id, node] of byUnit) {
    if (searchText(node).includes(needle)) matched.add(id);
  }

  const kept = new Set(matched);
  const pending = [...matched];
  while (pending.length > 0) {
    const node = byUnit.get(pending.pop()!)!;
    if (SEPARATE_LEVELS.includes(node.item_type)) continue;
    const parentLevel = levels.get(node.item_type)?.parent_level_code;
    const parent = node.parent_key && parentLevel ? unitId(parentLevel, node.parent_key) : null;
    const parents =
      parent && byUnit.has(parent)
        ? [parent]
        : node.associations.map((ref) => unitId(ref.item_type, ref.item_key));
    for (const ref of parents) {
      if (!byUnit.has(ref) || kept.has(ref)) continue;
      kept.add(ref);
      pending.push(ref);
    }
  }

  return {
    nodes: visible.filter((node) => kept.has(unitId(node.item_type, node.item_key))),
    matched,
  };
}

/** Objetos distintos numa lista de linhas: a unidade N:M repetida conta uma vez. */
function countUnits(rows: OrgTreeRow[]): number {
  const ids = new Set<string>();
  const walk = (list: OrgTreeRow[]) => {
    for (const row of list) {
      if (row.kind === "empty") continue;
      if (row.kind === "unit") ids.add(unitId(row.node.item_type, row.node.item_key));
      walk(row.children);
    }
  };
  walk(rows);
  return ids.size;
}

/**
 * As unidades que o lote pode decidir, na árvore inteira (abertas ou não):
 * a herdada fica de fora, porque o servidor a recusa (409), e o ancestral que a
 * busca só mostra como contexto também (D43): marcá-lo sem querer levaria a árvore
 * abaixo dele. A N:M, que aparece
 * debaixo de cada pai, é uma unidade só. Quem cruza com o papel é a tela.
 */
export function selectableUnitIds(rows: OrgTreeRow[]): string[] {
  return collectSelectable(rows, () => true);
}

/**
 * Dentre as selecionáveis, só as pendentes (D41): sem decisão efetiva. "Validar com
 * o cliente" já é uma decisão, então não entra. Mesma varredura e mesma regra da
 * N:M, uma unidade só.
 */
export function selectablePendingIds(rows: OrgTreeRow[]): string[] {
  return collectSelectable(rows, (node) => (node.effective_decision ?? null) === null);
}

function collectSelectable(
  rows: OrgTreeRow[],
  accept: (node: OrgValidationNode) => boolean,
): string[] {
  const ids = new Set<string>();
  const walk = (list: OrgTreeRow[]) => {
    for (const row of list) {
      if (row.kind === "empty") continue;
      if (row.kind === "unit" && row.matched && !row.node.scope_inherited && accept(row.node)) {
        ids.add(orgUnitId(row.node));
      }
      walk(row.children);
    }
  };
  walk(rows);
  return [...ids];
}

/** Os indicadores da tela, sobre o que o filtro mostra; a unidade N:M conta uma vez. */
export function orgIndicators(tree: OrgValidationTree, filter: readonly string[]): OrgIndicators {
  const nodes = visibleNodes(tree, filter);
  const decisionCount = (list: OrgValidationNode[], value: OrgValidationNode["effective_decision"]) =>
    list.filter((node) => (node.effective_decision ?? null) === value).length;

  const companyCodes = nodes.filter((node) => node.item_type === COMPANY_CODE);
  const ccInScope = decisionCount(companyCodes, "in_scope");
  const ccOut = decisionCount(companyCodes, "out_of_scope");

  const inScope = decisionCount(nodes, "in_scope");
  const outOfScope = decisionCount(nodes, "out_of_scope");
  const total = nodes.length;

  const counts = new Map<string, number>();
  for (const node of nodes) counts.set(node.item_type, (counts.get(node.item_type) ?? 0) + 1);
  const byType = [...tree.levels]
    .sort((a, b) => a.sequence - b.sequence)
    .filter((level) => counts.has(level.level_code))
    .map((level) => ({ levelCode: level.level_code, count: counts.get(level.level_code)! }));

  return {
    companyCodes: {
      total: companyCodes.length,
      inScope: ccInScope,
      outOfScope: ccOut,
      open: companyCodes.length - ccInScope - ccOut,
    },
    validation: {
      total,
      inScope,
      clientValidation: decisionCount(nodes, "client_validation"),
      outOfScope,
      pending: decisionCount(nodes, null),
      decidedPct: total === 0 ? 0 : Math.round(((inScope + outOfScope) / total) * 100),
    },
    mismatches: nodes.filter((node) => node.validation.data_matches === false).length,
    byType,
  };
}

/** As linhas que abrem e fecham: unidade com filho e o grupo do que não tem empresa. */
function openableRows(rows: OrgTreeRow[]): Array<OrgUnitRow | OrgGroupRow> {
  return rows.flatMap((row) =>
    row.kind === "empty"
      ? []
      : [...(row.children.length > 0 ? [row] : []), ...openableRows(row.children)],
  );
}

/** "Expandir tudo": toda unidade com filho fica aberta, e o grupo também. */
export function expandAllKeys(rows: OrgTreeRow[]): Set<string> {
  return new Set(openableRows(rows).map((row) => row.key));
}

/**
 * Como a árvore abre, e como fica depois de trocar o filtro: tudo aberto, menos o
 * grupo do que não tem empresa, que são centenas de linhas num sistema real. O
 * bloco das organizações de compras abre aberto: são poucas, e é ele que mostra
 * a quem cada uma atende.
 */
export function initialExpandedKeys(rows: OrgTreeRow[]): Set<string> {
  return new Set(
    openableRows(rows)
      .filter((row) => row.kind !== "group" || row.group !== SHARED_GROUP)
      .map((row) => row.key),
  );
}

/**
 * "Recolher": a árvore para no nível de company code. O que está acima dela (os
 * transversais e a empresa) continua aberto, e a company code fica fechada. O
 * grupo do que não tem empresa também fecha.
 */
export function collapseToCompanyCodeKeys(rows: OrgTreeRow[]): Set<string> {
  const open = new Set<string>();
  const walk = (list: OrgTreeRow[]) => {
    for (const row of list) {
      if (row.kind !== "unit" || row.node.item_type === COMPANY_CODE) continue;
      if (row.children.length > 0) open.add(row.key);
      walk(row.children);
    }
  };
  walk(rows);
  return open;
}

/**
 * Níveis cuja chave no SAP é composta com a do pai: o depósito é `WERKS/LGORT`, e
 * o `id` sozinho ("0001") se repete em todo centro. Nos demais, a chave legado é
 * o próprio `id`, e o `item_key` só carrega o pai para ser única no banco.
 */
const COMPOSITE_KEY_LEVELS: ReadonlySet<string> = new Set(["storage_location", "sales_area"]);

/** A chave do objeto no sistema de origem, como a tela e o resumo mostram. */
export function legacyKey(node: OrgValidationNode): string {
  const raw = COMPOSITE_KEY_LEVELS.has(node.item_type) ? node.item_key : node.id;
  return raw.split("|").join("/");
}
